import {Injectable} from '@angular/core';
import {HttpService} from './http.service';
import {AuthRequest} from '../../interfaces/core/auth-request';
import {AuthRequestFactory} from '../../factories/core/auth-request-factory';
import {lastValueFrom, Observable, Subject} from 'rxjs';
import {AuthToken} from '../../interfaces/core/auth-token';
import {map} from 'rxjs/operators';
import {AuthTokenFactory} from '../../factories/core/auth-token-factory';
import {StorageService} from './storage.service';
import {ErrorMessagesService} from './error-messages.service';
import {ErrorMessageType} from '../../enums/core/error-message-type';
import {AuthException} from '../../exceptions/auth.exception';
import {AuthExceptionType} from '../../enums/core/auth-exception-type';
import {AppResetService} from './app-reset.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public static readonly storageKey: string = 'auth-token';

  private static _authToken: AuthToken = null;
  private readonly _logout$: Subject<void>;

  private _isLoginLocked: boolean = false;


  /**
   *
   * @param httpService
   * @param errorMessagesService
   * @param appResetService
   * @param storageService
   */
  constructor(private httpService: HttpService,
              private errorMessagesService: ErrorMessagesService,
              private appResetService: AppResetService,
              private storageService: StorageService) {
    this._logout$ = new Subject<void>();
  }

  get logout$(): Subject<void> {
    return this._logout$;
  }

  get isLoginLocked(): boolean {
    return this._isLoginLocked;
  }

  public async lockLogin(): Promise<void> {
    this._isLoginLocked = true;
    await this.storageService.setItem('is-login-locked', this._isLoginLocked);
  }

  public async unlockLogin(): Promise<void> {
    this._isLoginLocked = false;
    await this.storageService.setItem('is-login-locked', this._isLoginLocked);
  }

  /**
   *
   */
  public static getAuthToken(): AuthToken {
    return AuthService._authToken;
  }

  /**
   *
   */
  public static isAuthenticated(): boolean {
    return !!AuthService.getAuthToken();
  }

  /**
   *
   */
  public async init(): Promise<void> {
    AuthService._authToken = await this.storageService.getItem(AuthService.storageKey);
    this._isLoginLocked = await this.storageService.getItem('is-login-locked');
  }

  /**
   *
   * @param authRequest
   */
  public async authenticate(authRequest: AuthRequest): Promise<AuthToken> {
    try {
      const authToken = await lastValueFrom(this.loadAuthToken(authRequest));
      await this.setAuthenticated(authToken);
      return authToken;
    }
    catch(error: any) {
      if(error?.status === 422) {
        throw new AuthException(AuthExceptionType.Credentials, 'Wrong credentials');
      }
      console.log(error);
      throw new AuthException(AuthExceptionType.Unknown, 'Auth failed', 143);
    }
  }

  /**
   *
   */
  public async softLogout(): Promise<void> {
    await this.removeAuthToken();
    // await this.appResetService.softReset();
    this._logout$.next();
  }

  /**
   *
   */
  public async logout(): Promise<void> {
    await this.removeAuthToken();
    await this.appResetService.hardReset();
    this._logout$.next();
  }

  /**
   *
   */
  public loadCurrentVersionCode(): Observable<number> {
    return this.httpService.get<{ version_code: number }>({
      url: ['version-code']
    }).pipe(map((_response) => {
      return _response.version_code;
    }));
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Private methods
  // -------------------------------------------------------------------------------------------------------------------
  /**
   *
   */
  private async setAuthenticated(_authToken: AuthToken): Promise<void> {
    AuthService._authToken = _authToken;
    try {
      await this.storageService.setItem(AuthService.storageKey, AuthService._authToken);
    }
    catch(error) {
      this.errorMessagesService.showErrorMessage(ErrorMessageType.Storage, 200).finally();
      throw error;
    }
  }

  /**
   *
   */
  private async removeAuthToken(): Promise<void> {
    AuthService._authToken = null;
    await StorageService.removeItem(AuthService.storageKey);
  }

  /**
   *
   * @param authRequest
   * @private
   */
  private loadAuthToken(authRequest: AuthRequest): Observable<AuthToken> {
    return this.httpService.post<any>({
      url: ['auth', 'authenticate']
    }, AuthRequestFactory.toRemote(authRequest))
      .pipe(map((_authToken) => AuthTokenFactory.fromObject(_authToken)));
  }

}
